import React, { Fragment, useCallback, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useDispatch } from 'react-redux'
import { setModal } from '../../redux/application-slice'
import { Checkbox } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { MODAL_TYPES } from './modal-controller'

interface ProcessSupplementaryConditionsModalProps {
  open: boolean
}

const ProcessSupplementaryConditionsModal: React.FC<
  ProcessSupplementaryConditionsModalProps
> = ({ open }) => {
  const dispatch = useDispatch()
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [showModal, setShowModal] = useLocalStorage<boolean>({
    key: 'show-process-sc-modal',
    defaultValue: true,
    getInitialValueInEffect: true,
  })

  const onClose = useCallback(() => {
    if (dontShowAgain) {
      setShowModal(false)
    }
    dispatch(setModal(null))
    setTimeout(() => {
      dispatch(
        setModal({ modal: MODAL_TYPES.EDIT_PROJECT_RISK_LIST_TEMPLATES })
      )
    }, 100)
  }, [dispatch, dontShowAgain, setShowModal])

  const onChangeDontShowAgain = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDontShowAgain(event.currentTarget.checked)
    },
    [setDontShowAgain]
  )

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mt-3">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      How to Use Supplementary Conditions
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        If you uploaded a supplemental condition document along
                        with a base document, you can run a redline by starting
                        the analysis as shown below. For more information, refer
                        to our help center in the top right.
                      </p>
                      <div className="mt-4">
                        <img
                          src="https://storage.googleapis.com/provision-public/sc_options.gif"
                          alt="How to use supplementary conditions"
                          className="w-full rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    onClick={onClose}
                  >
                    Got it
                  </button>
                </div>
                <div className="relative mt-5 flex items-center">
                  <div className="flex h-6 items-center">
                    <input
                      id="dont-show-again-checkbox"
                      aria-describedby="dont-show-again-description"
                      name="dont-show-again"
                      type="checkbox"
                      checked={dontShowAgain}
                      onChange={onChangeDontShowAgain}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="dont-show-again-checkbox"
                      className="cursor-pointer font-medium text-gray-900"
                    >
                      <div className="text-xs">Don't show this again</div>
                    </label>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default ProcessSupplementaryConditionsModal
